import React, { lazy, Suspense, useState } from 'react'
import { css } from '@linaria/core'
import { colors, LayoutBand, Row, AtContainerWidth } from '@fs/zion-ui'
import { ResponsiveProvider } from '@fs/zion-ui/quarks-authorized-use-only'
import { useShowTemple } from '@fs/zion-permissions'
import useShowArabicExperience from './hooks/useShowArabicExperience'
import useFooterExpansion from './hooks/useFooterExpansion'
import MinNavLinks from './components/NavLinks'
import MinLegalText from './components/LegalText'
import FooterCollapseButton from './FooterCollapseButton'
import DynamicLinksProvider from './DynamicLinksProvider'

const MinChurchLogo = lazy(() => import('./ChurchLogo'))
const FooterExpansion = lazy(() => import('./FooterExpansion'))

// Changes to any version of the footer should be updated on our footer docs
// Footer Documentation: https://icseng.atlassian.net/wiki/spaces/Product/pages/133633618/Footer+Documentation

const accentColor = colors.gray02

const collapsedFooterCss = css`
  font-size: 14px;
  background-color: ${colors.gray00};
  border-top: 10px solid ${accentColor};
`

const mobileLegalCss = css`
  text-align: center;
`

const legalBandCss = css`
  min-height: 80px;
`

const atWidthConfig = { default: 'small', md: 'mid', xl: 'wide' }

function Footer({
  menaReligiousMode = false,
  nonReligiousMode = true,
  canSeeTemple,
  external = false,
  hideLinksBand = false,
  hideMoreButton = false,
  defaultExpanded = false,
  ...props
}) {
  const [showTemple] = useShowTemple()
  const effectiveCanSeeTemple = canSeeTemple ?? showTemple
  const showArabicExperience = useShowArabicExperience()
  const [open, setOpen] = useFooterExpansion(showArabicExperience, defaultExpanded)
  const [hasInteracted, setHasInteracted] = useState(false)

  const logo = !menaReligiousMode && !nonReligiousMode && !showArabicExperience && (
    <Suspense fallback="">
      <MinChurchLogo />
    </Suspense>
  )

  const legalText = (
    <MinLegalText
      nonReligiousMode={nonReligiousMode}
      menaReligiousMode={menaReligiousMode}
      showArabicExperience={showArabicExperience}
      external={external}
    />
  )

  return (
    <footer className={collapsedFooterCss} {...props}>
      {/* ResponsiveProvider is here because we aren't wrapped in a responsive parent item, just `footer` */}
      {/* We need this so the footer responds to its container, not the page, in case the footer is affected by a side panel */}
      <ResponsiveProvider>
        <AtContainerWidth>
          {(atWidth) => (
            <>
              {!hideLinksBand && (
                <LayoutBand marginY="xs" maxWidth="md">
                  {/* including the hideMoreButton check here so a move from a page w/More button to one without doesn't leave it open & broken */}
                  {!showArabicExperience && (!open || hideMoreButton) && (
                    <MinNavLinks
                      showArabicExperience={showArabicExperience}
                      viewWidth={atWidth(atWidthConfig)}
                      external={external}
                      handleExpandClick={() => {
                        setOpen(true)
                        setHasInteracted(true)
                      }}
                      hideMoreButton={hideMoreButton}
                      shouldFocus={hasInteracted && !open}
                    />
                  )}
                  {/* Hide expansion UI entirely for non-member Arabic users since they cannot expand */}
                  {!showArabicExperience && !hideMoreButton && (
                    <>
                      {open && (
                        <FooterCollapseButton
                          onClick={() => {
                            setOpen(false)
                            setHasInteracted(true)
                          }}
                          shouldFocus={hasInteracted}
                        />
                      )}
                      <Suspense fallback="">
                        <FooterExpansion
                          nonReligiousMode={nonReligiousMode}
                          canSeeTemple={effectiveCanSeeTemple}
                          expanderConfig={{ open, setOpen }}
                          external={external}
                        />
                      </Suspense>
                    </>
                  )}
                </LayoutBand>
              )}
              <LayoutBand color={accentColor} marginY={atWidth(atWidthConfig) === 'small' ? 'sm' : 'xs'} maxWidth="md">
                {atWidth(atWidthConfig) === 'small' ? (
                  <Row alignX="center" wrap>
                    {logo}
                    <div className={mobileLegalCss}>{legalText}</div>
                  </Row>
                ) : (
                  <Row alignY="middle" className={legalBandCss}>
                    <div growX>{legalText}</div>
                    <div noShrinkX>{logo}</div>
                  </Row>
                )}
              </LayoutBand>
            </>
          )}
        </AtContainerWidth>
      </ResponsiveProvider>
    </footer>
  )
}

export default function FooterWithProviders(props) {
  return (
    <DynamicLinksProvider>
      <Footer {...props} />
    </DynamicLinksProvider>
  )
}
